import type { ReceiptDetail, UpdateReceiptParams } from '@/api/types/receipt'

import { FIELD_LABELS } from '../field-labels'
import { fromDateTimeLocal, toDateTimeLocal, toNumberOrNull, toText } from '../field-value'
import { EditableField } from './editable-field'

type FieldKey = keyof UpdateReceiptParams & keyof ReceiptDetail

type FieldSpec = {
  key: FieldKey
  kind: 'text' | 'number'
}

const SECTIONS: readonly { title: string; fields: readonly FieldSpec[] }[] = [
  {
    title: '店舗',
    fields: [
      { key: 'store_name', kind: 'text' },
      { key: 'store_company', kind: 'text' },
      { key: 'store_branch', kind: 'text' },
      { key: 'store_tel', kind: 'text' },
      { key: 'store_address', kind: 'text' },
      { key: 'store_invoice_no', kind: 'text' },
      { key: 'receipt_type', kind: 'text' },
      { key: 'payment_method', kind: 'text' },
    ],
  },
  {
    title: '金額',
    fields: [
      { key: 'subtotal', kind: 'number' },
      { key: 'tax_amount', kind: 'number' },
      { key: 'total_amount', kind: 'number' },
      { key: 'discount_total', kind: 'number' },
      { key: 'stated_item_count', kind: 'number' },
      { key: 'paid_amount', kind: 'number' },
      { key: 'change_amount', kind: 'number' },
    ],
  },
  {
    title: '税区分',
    fields: [
      { key: 'tax8_target', kind: 'number' },
      { key: 'tax8_amount', kind: 'number' },
      { key: 'tax10_target', kind: 'number' },
      { key: 'tax10_amount', kind: 'number' },
    ],
  },
]

type Props = {
  receipt: ReceiptDetail
  onChange: (params: UpdateReceiptParams) => void
}

export const ReceiptFieldGrid = ({ receipt, onChange }: Props) => {
  const editedFields = new Set(receipt.edited_fields)

  return (
    <div className="flex flex-col gap-4">
      <EditableField
        label={FIELD_LABELS.purchased_at}
        type="datetime-local"
        value={toDateTimeLocal(receipt.purchased_at)}
        isEdited={editedFields.has('purchased_at')}
        onCommit={(value) => onChange({ purchased_at: fromDateTimeLocal(value) })}
      />

      {SECTIONS.map((section) => (
        <section key={section.title} className="flex flex-col gap-2">
          <h3 className="text-sm text-muted-foreground">{section.title}</h3>
          <div className="grid grid-cols-2 gap-2">
            {section.fields.map((field) => (
              <EditableField
                key={field.key}
                label={FIELD_LABELS[field.key]}
                type={field.kind === 'number' ? 'number' : 'text'}
                value={toText(receipt[field.key])}
                isEdited={editedFields.has(field.key)}
                onCommit={(value) =>
                  onChange({ [field.key]: field.kind === 'number' ? toNumberOrNull(value) : value || null })
                }
              />
            ))}
          </div>
        </section>
      ))}

      <EditableField
        label={FIELD_LABELS.notes}
        value={toText(receipt.notes)}
        isEdited={editedFields.has('notes')}
        onCommit={(value) => onChange({ notes: value || null })}
      />
    </div>
  )
}
